import React, { useContext, useEffect, useState, FC } from 'react';

import { useQuery } from '@apollo/client';
import { Button } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { useInfiniteHits } from 'react-instantsearch-hooks-web';

import { UserInfoCtx } from '../../../../containers/CommonLayout/CommonLayout';
import { DiscoverContext } from '../../../../context/DiscoverContext';
import {
  GET_DISCOVER_PATHWAYS_DETAILED_INFO,
  GET_VIDEOS_EXIST_IN_USER_WATCH_LIST,
} from '../../../../graphql/pathways';
import { GET_VIDEO_PROGRESS_LIST } from '../../../../graphql/videos';
import { useIsWidthUp } from '../../../../theme';
import {
  Query,
  QueryGet_Discover_Pathways_Detailed_InfoArgs,
  QueryListVideoProgressArgs,
  QueryVideosExistInUserWatchListArgs,
} from '../../../../types';
import { collectValueByKey } from '../../../../utils/helpers';
import { useSERPNavigation } from '../../../../utils/hooks';
import { HITS_TYPES } from '../../const';
import type { AggregatorHit } from '../../const';
import { GlossaryGridHit } from '../GlossaryGridHit';
import { GlossaryInfiniteHist } from '../GlossaryInfiniteHits';
import GridHit from '../GridHit';
import { Hit } from '../Hit';
import useStyles from './styles';

interface InfiniteHitsProps {
  tenantTitle?: string | null;
  tenantName?: string | null;
  page: number | null;
  limit: number;
  view: string;
  total: number | null;
  linkedInId?: string | null;
}

export const InfiniteHits: FC<InfiniteHitsProps> = ({
  page,
  limit,
  total,
  view,
  tenantTitle,
  tenantName = null,
}) => {
  const { t } = useTranslation();
  const classes = useStyles(view);
  const isDesktop = useIsWidthUp('md');

  const isGridView = view === 'grid' && isDesktop;
  const showedItems = page ? (page + 1) * limit : limit;

  const serpType = useSERPNavigation();
  const onGlossaryPage = serpType === HITS_TYPES.GLOSSARY;
  const onVideoPage = serpType === HITS_TYPES.VIDEO;

  const { searchState } = useContext(DiscoverContext);
  const { userInfo } = useContext(UserInfoCtx);
  const { hits, isLastPage, showMore, sendEvent } = useInfiniteHits<AggregatorHit>();

  const [pathwayIds, setPathwayIds] = useState<number[]>([]);
  const [videoIds, setVideoIds] = useState<string[]>([]);

  const { data: pathwaysProgressData } = useQuery<
    Pick<Query, 'get_discover_pathways_detailed_info'>,
    QueryGet_Discover_Pathways_Detailed_InfoArgs
  >(GET_DISCOVER_PATHWAYS_DETAILED_INFO, {
    variables: {
      pathway_ids: pathwayIds,
    },
    skip: pathwayIds.length === 0 || isGridView || !userInfo,
    fetchPolicy: 'network-only',
  });

  const pathwaysProgress = pathwaysProgressData?.get_discover_pathways_detailed_info || [];

  const { data: videoProgressData } = useQuery<
    Pick<Query, 'listVideoProgress'>,
    QueryListVideoProgressArgs
  >(GET_VIDEO_PROGRESS_LIST, {
    variables: {
      filter: {
        user_id: userInfo?.id || '',
        video_ids: videoIds,
      },
    },
    skip: !userInfo?.id || videoIds.length === 0 || isGridView,
    fetchPolicy: 'network-only',
  });

  const videosProgress = videoProgressData?.listVideoProgress || [];

  //fill video ids for all pages except non-video search type pages (pathways, insights, etc.)
  const videosIds =
    onVideoPage || !serpType
      ? hits
          ?.filter((entity) => entity?.searchable_type === HITS_TYPES.VIDEO)
          ?.map((entity) => entity?.id)
          ?.filter((item): item is number => Boolean(item))
          ?.map((item) => item.toString())
      : null;

  const { data: videosInWatchlist } = useQuery<
    Pick<Query, 'VideosExistInUserWatchList'>,
    QueryVideosExistInUserWatchListArgs
  >(GET_VIDEOS_EXIST_IN_USER_WATCH_LIST, {
    variables: {
      video_ids: videosIds,
    },
    skip: !videosIds || !videosIds?.length || !userInfo,
  });

  const watchlistById =
    videosInWatchlist?.VideosExistInUserWatchList?.reduce(
      collectValueByKey('video_id', 'exists'),
      {},
    ) || {};

  useEffect(() => {
    if (hits && hits.length > 0) {
      const newVideosIds = hits
        .filter((item) => item?.searchable_type === HITS_TYPES.VIDEO)
        .map((item) => item?.id?.toString());
      setVideoIds(newVideosIds);

      const newPathwaysIds = hits
        .filter((item) => item?.searchable_type === HITS_TYPES.PATHWAY)
        .map((item) => item.id);
      setPathwayIds(newPathwaysIds);
    }
  }, [hits]);

  return (
    <div className={classes.wrapper}>
      <ul
        className={cx(
          isGridView ? 'ais-InfiniteHits-grid' : 'ais-InfiniteHits-list',
          onGlossaryPage && 'ais-GlossaryInfiniteHits-list',
        )}
      >
        {onGlossaryPage && !searchState?.query ? (
          <GlossaryInfiniteHist
            hits={hits}
            tenantTitle={tenantTitle}
            isGridView={isGridView}
            sendEvent={sendEvent}
          />
        ) : (
          hits.map((hit) => {
            const pathwayProgress = pathwaysProgress.find(
              (item) => item?.id === hit?.id && hit?.searchable_type === HITS_TYPES.PATHWAY,
            );
            const videoProgress = videosProgress.find(
              (item) =>
                item?.video?.id === hit?.id.toString() && hit?.searchable_type === HITS_TYPES.VIDEO,
            );

            return (
              <li data-testid="resultItem" key={hit.objectID}>
                {isGridView ? (
                  hit?.searchable_type === HITS_TYPES.GLOSSARY ? (
                    <GlossaryGridHit hit={hit} sendEvent={sendEvent} />
                  ) : (
                    <GridHit hit={hit} sendEvent={sendEvent} />
                  )
                ) : (
                  <Hit
                    hit={hit}
                    pathwayProgress={pathwayProgress}
                    videoProgress={videoProgress}
                    tenantTitle={tenantTitle}
                    tenantName={tenantName}
                    isInWatchlist={watchlistById[hit.id]}
                    sendEvent={sendEvent}
                  />
                )}
              </li>
            );
          })
        )}
      </ul>
      {!isLastPage && (
        <Button className={classes.viewBtn} onClick={showMore}>
          {t('button.viewMore')} - {showedItems} {t('button.of')} {total}
        </Button>
      )}
    </div>
  );
};

export default InfiniteHits;
